import React, { useState, useEffect, useCallback, useRef } from 'react';
import { get, toNumber } from 'lodash';
import { useMutation, useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import IconDiscount from 'components/icons/iconDiscount';
import CloseIcon from 'components/icons/iconClose';
import { useClickAway } from 'react-use';
import getZipcode from 'commons/getZipcode';
import ProtectionPlanButtonAndModal from 'components/consumer/ProtectionPlanButtonAndModal';
import Currency from 'components/consumer/Currency';
import { GET_CART, GET_CHECKOUT_SUMMARY } from 'mocks/queries';
import { UPDATE_CART_ITEM_PROTECTION_PLAN } from 'mocks/mutations';
import { getCheckoutRef } from 'components/consumer/checkout/utils/checkout';
import { logError } from 'commons/logger';
import parseCartItemErrors from 'commons/parseCartItemErrors';
import { colorGray } from 'styles/layout/colors';
import IconInfoNew from 'components/icons/IconInfoNew';
import IconInStock from 'components/icons/iconInStock';
import IconMadeToOrder from 'components/icons/iconMadeToOrder';
import QuantitySelect from './quantity-select';
import ProductImage from './components/ProductImage';
import ProductDescription from './components/ProductDescription';
import StatusLabel from '../StatusLabel';
import { getProductStatus } from './utils';

const CartProduct = ({
  cartToken,
  closeCart,
  openProtectionModal,
  hasImageLink,
  product,
  removeExtraCushion,
  removeItem,
  removeItemLoading,
  viewType = '',
}) => {
  const { issue } = parseCartItemErrors(product && product.cart_item_errors);
  const [isRemovePPLoading, setIsRemovePPLoading] = useState(false);
  const [isRemovePPError, setIsRemovePPError] = useState(null);
  const [isDisplayClearanceTooltip, setDisplayClearanceTooltip] = useState(
    false
  );
  const tooltipRef = useRef(null);
  const [getCart] = useLazyQuery(GET_CART, { fetchPolicy: 'network-only' });
  const [getCheckoutSummary] = useLazyQuery(GET_CHECKOUT_SUMMARY, {
    fetchPolicy: 'network-only',
  });
  const [updateCartItemProtectionPlanMutation] = useMutation(
    UPDATE_CART_ITEM_PROTECTION_PLAN
  );

  const closeTooltip = useCallback(() => {
    setDisplayClearanceTooltip(prevDisplayClearanceTooltip => {
      if (prevDisplayClearanceTooltip) return false;
      return prevDisplayClearanceTooltip;
    });
  }, []);

  useClickAway(tooltipRef, closeTooltip);
  const removeProtectionPlanOnCartItem = useCallback(async () => {
    const cartItemId = product.id;
    try {
      let success = null;
      try {
        const response = await updateCartItemProtectionPlanMutation({
          variables: {
            cart_id: toNumber(cartItemId),
            plan_id: null,
            protection_plan: false,
          },
        });

        success = get(response, 'data.updateCartItemProtectionPlan', null);
      } catch (e) {
        setIsRemovePPError('Error occurred. Please try again.');
      }
      if (success) {
        const zipCode = getZipcode();
        const checkoutRef = getCheckoutRef();
        await Promise.all([
          getCart({
            context: { noBatch: true },
            variables: {
              zipCode,
              ...(checkoutRef ? { checkoutRef } : {}),
            },
          }),
          getCheckoutSummary({
            context: { noBatch: true },
            variables: {
              ...(checkoutRef ? { checkoutRef } : {}),
              input: {
                zipcode: zipCode,
              },
            },
          }),
        ]);
      }
      setIsRemovePPLoading(false);
    } catch (e) {
      setIsRemovePPLoading(false);
    }
  }, [
    getCart,
    getCheckoutSummary,
    product.id,
    updateCartItemProtectionPlanMutation,
  ]);

  useEffect(() => {
    if (isRemovePPLoading) {
      removeProtectionPlanOnCartItem().catch(logError);
      setIsRemovePPError(null);
    }
  }, [isRemovePPLoading, removeProtectionPlanOnCartItem]);

  const productStatus = getProductStatus(product);

  return (
    <div
      className="border-b border-solid border-gray-light7 py-8 px-0 md:py-5 m-0 md:m-[unset] last:border-b-0"
      data-xc="cart-product"
    >
      <div className="p-0">
        <div className="flex flex-wrap flex-row items-stretch relative last:border-b-0">
          <div className="basis-[33.33333%] min-w-0">
            <ProductImage
              closeCart={closeCart}
              hasImageLink={hasImageLink}
              product={product}
              height={80}
              key={product?.id}
            />
          </div>

          <div className="basis-[41.66667%] min-w-0">
            <h2 className="[&_span]:text-gray m-0 text-base font-bold text-gray leading-none">
              {product.name}
            </h2>

            <div className="[&_span]:text-gray text-gray-light3 text-xs flex flex-col my-2 mx-0">
              <ProductDescription
                product={product}
                removeExtraCushion={removeExtraCushion}
              />
            </div>

            <div className="w-16 text-base text-gray">
              <QuantitySelect
                issue={issue}
                product={product}
                removeProduct={removeItem}
                removeProductLoading={removeItemLoading}
                cartToken={cartToken}
                viewType={viewType}
              />
            </div>
            <div className="material-section flex pr-8 last:pr-0" />
          </div>

          <div className="basis-1/4 min-w-0">
            <div className="text-right text-base flex flex-col justify-between h-full">
              <div className="cart-price-and-clearance">
                <div className="flex flex-col items-end">
                  <div className="text-sm font-bold text-right">
                    {product.discount_amount > 0 ? (
                      <>
                        <span className="line-through text-gray mr-2">
                          <Currency className="text-gray">
                            {product.price_pre_discount}
                          </Currency>
                        </span>
                        <span className="text-red">
                          <Currency>{product.price}</Currency>
                        </span>
                      </>
                    ) : (
                      <span className="text-gray">
                        <Currency>{product.price}</Currency>
                      </span>
                    )}
                  </div>
                  {productStatus && (
                    <button
                      type="button"
                      className="mt-1 cursor-pointer"
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        setDisplayClearanceTooltip(prev => !prev);
                      }}
                    >
                      <StatusLabel className="inline-flex items-center px-1.5 py-0.5 text-[0.66rem] whitespace-nowrap">
                        {productStatus.bannerText}
                        {productStatus.type === 'ready_to_ship' && (
                          <IconInStock
                            width={15}
                            height={15}
                            className="ml-1"
                          />
                        )}
                        {productStatus.type === 'made_to_order' && (
                          <IconMadeToOrder
                            width={11}
                            height={11}
                            className="ml-1"
                          />
                        )}
                        {productStatus.type === 'final_sale' && (
                          <IconInfoNew
                            width={13}
                            height={13}
                            className="ml-1"
                          />
                        )}
                      </StatusLabel>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {isDisplayClearanceTooltip && productStatus && (
          <div
            className="relative flex flex-row p-4 my-2.5 bg-yellow-light"
            id={`clearance-${product.id}`}
            ref={tooltipRef}
          >
            <button
              onClick={event => {
                event.stopPropagation();
                closeTooltip();
              }}
              type="button"
              className="absolute right-2.5 top-2.5 w-7 h-7 flex justify-center items-center"
            >
              <CloseIcon color={colorGray} width="12" height="12" />
            </button>
            {productStatus.type === 'ready_to_ship' && (
              <IconInStock width="100" height="24" className="mr-2.5" />
            )}
            {productStatus.type === 'made_to_order' && (
              <IconMadeToOrder width="100" height="24" className="mr-2.5" />
            )}
            {productStatus.type === 'final_sale' && (
              <IconDiscount width="100" className="mr-2.5" />
            )}
            <div className="text-sm">
              {productStatus.bannerText}
              <div className="text-xs">{productStatus.contentText}</div>
            </div>
          </div>
        )}
        {issue ? (
          <div className="text-sm font-bold text-red mt-4">
            {`${issue} Please remove this item from the cart to continue to checkout.`}
          </div>
        ) : (
          product.eligible_protection_plans?.length > 0 && (
            <ProtectionPlanButtonAndModal
              eligible_plans={product.eligible_protection_plans}
              forPage="cart"
              isRemovePPError={isRemovePPError}
              isRemovePPLoading={isRemovePPLoading}
              openModal={openProtectionModal}
              productId={product.id}
              removeProtectionPlan={setIsRemovePPLoading}
              selectedProtectionPlan={product.selected_protection_plan}
            />
          )
        )}
      </div>
    </div>
  );
};

CartProduct.propTypes = {
  cartToken: PropTypes.string,
  closeCart: PropTypes.func,
  openProtectionModal: PropTypes.func,
  hasImageLink: PropTypes.bool,
  product: PropTypes.shape({
    cart_item_errors: PropTypes.arrayOf(PropTypes.object),
    discount_amount: PropTypes.number,
    eligible_protection_plans: PropTypes.arrayOf(PropTypes.object),
    id: PropTypes.string,
    name: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    pickLater: PropTypes.bool,
    price_pre_discount: PropTypes.number,
    price: PropTypes.number,
    flash_sale: PropTypes.bool,
    is_clearance: PropTypes.bool,
    selected_protection_plan: PropTypes.object,
    stock_discount: PropTypes.number,
    product: PropTypes.shape({
      clearance_stock: PropTypes.object,
    }),
    sku: PropTypes.string,
  }),
  removeExtraCushion: PropTypes.func,
  removeItem: PropTypes.func,
  removeItemLoading: PropTypes.bool,
  viewType: PropTypes.string,
};

export default CartProduct;
